// An access point's own SSID ceiling on its page in Operate (ADR-0130
// decision 1c; #809): what the device last said its radios can carry, read
// from the device on an operator's click and recorded, with when.
//
// **The read is the operator's act and this is its one control.** One click is
// one read: nothing here reads on a timer, on a page load or after a plan, and
// a device that is not online is not offered it. The control is drawn for a
// caller holding `device.command` at the site, as the bounded commands are.
//
// **The figures are shown as the device printed them**, per radio with the
// bands it fronts and, where the device prints them, per hardware index, and
// folded into no single number. ADR-0130 decision 3 is ruled by ADR-0429 (per
// radio where a radio fronts one band, one bound across the device for
// ath12k's one radio fronting three), and a plan applies it to this recording
// (#1879); which bound applied is Design › Wireless › AP ↔ SSID's to show.
import { api, idempotencyKey } from "../../api/client";
import { useQuery } from "../../api/hooks";
import { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { Info, ProblemBox, When } from "../../ui";

type Phy = components["schemas"]["SsidCeilingPhy"];
type Combination = components["schemas"]["SsidCeilingCombination"];

/** One combination in words: its AP bound and its total, and its hardware
 * index where the device printed one. */
export function combinationWords(c: Combination): string {
  const parts: string[] = [];
  if (c.ap_limit !== undefined) parts.push(`${c.ap_limit} AP`);
  if (c.total_limit !== undefined) parts.push(`${c.total_limit} in all`);
  const said = parts.join(", ");
  return c.hw_index !== undefined ? `hardware ${c.hw_index}: ${said}` : said;
}

/** A band by iw's number, in words: 1 is 2.4 GHz, 2 is 5 GHz, 3 is 60 GHz
 * and 4 is 6 GHz. Any other number is said as iw numbers it. */
export function bandWords(band: number): string {
  return ({ 1: "2.4 GHz", 2: "5 GHz", 3: "60 GHz", 4: "6 GHz" } as Record<number, string>)[band] ?? `band ${band}`;
}

/** One radio in words: the bands it fronts, then every combination it
 * advertised, in order. */
export function phyWords(p: Phy): string {
  const name = p.bands.length > 0 ? `${p.phy} (${p.bands.map(bandWords).join(", ")})` : p.phy;
  if (p.combinations.length === 0) return `${name}: no combination read`;
  return `${name}: ${p.combinations.map(combinationWords).join("; ")}`;
}

export default function SsidCeilingFact({ serial, online, mayRead }: {
  serial: string;
  /** Online by the one definition, as the page's device read says. */
  online: boolean;
  /** `device.command` at the device's site. */
  mayRead: boolean;
}) {
  const q = useQuery(() => api.GET("/devices/{serial}/ssid-ceiling", { params: { path: { serial } } }), [serial]);
  const m = useMutation();
  const read = async () => {
    const ok = await m.run(() => api.POST("/devices/{serial}/ssid-ceiling:read", {
      params: { path: { serial }, header: { "Idempotency-Key": idempotencyKey() } },
    }));
    if (ok) q.reload();
  };
  if (q.status === "loading" && !q.data) return <span className="muted">…</span>;
  if (q.status === "error" || !q.data) return <span className="muted">—</span>;
  const r = q.data;
  return (
    <>
      {r.recorded
        ? <>{(r.phys ?? []).map(phyWords).join(" · ")}{r.cut_short && <> (the answer was cut short, and its last line was not read)</>}{r.read_at && <> · read <When value={r.read_at} /></>}</>
        : <span className="muted">Not read from this access point</span>}
      {mayRead && online && (
        <> <button type="button" className="btn btn-quiet" disabled={m.busy} onClick={read}>
          {m.busy ? "Reading…" : r.recorded ? "Read again" : "Read from the device"}
        </button></>
      )}
      {/* Restates ADR-0130 decision 3 as ADR-0429 ruled it, and decisions 2a and 4b — a plan checks this access point against its recorded reading, per radio and across the bands one radio serves, and at the default limit where the reading cannot be applied (#1879). */}
      {r.recorded && <Info label="About these limits">Plans check this access point against these limits, one for each radio. A radio that serves several bands has one limit for all of them. Where a reading cannot be applied, plans use the default limit, and Design › Wireless › AP ↔ SSID says which applied.</Info>}
      {m.problem && <ProblemBox problem={m.problem} />}
    </>
  );
}
